<!-- The dialog title names both sections, so the cards carry no labels of their
     own — "CREDIT USAGE" and "REFUND" over the two cards only restated it. The
     cards themselves are still what separates reading the balance from acting
     on it. -->
<div class="vcr">
  <div class="vcr__head">
    <h4 class="vcr__title">Credit usage and refund</h4>
  </div>

  <!-- The scroller is the full width of the dialog; the 520px reading cap lives
       on the wrapper inside it. Capping the scroller itself parked the bar
       against the cards, floating in from the dialog edge with nothing but
       background either side of it. -->
  <div class="vcr__body" id="scrollcustom">
    <div class="vcr__body-inner">
      <!-- ══ section 1: usage ═════════════════════════════════════════════ -->
      <div class="vcr__card">
        <div class="vcr__ledger-top">
          <span class="vcr__ledger-label">Remaining</span>
          <span class="vcr__ledger-value">
            <b>{{ remaining == null ? '—' : remaining }}</b>
            <span class="vcr__ledger-of">of {{ purchased }} credits</span>
          </span>
        </div>

        <!-- One segment: spend is accounted for at the WALLET, not per action, so
             the bar reports the only quantity that is actually tracked. -->
        <div class="vcr__bar" *ngIf="purchased > 0 && remaining != null">
          <span class="vcr__bar-seg is-used" [style.width.%]="usedPct"></span>
        </div>

        <table class="vcr__tbl">
          <tr class="vcr__row vcr__row--dim">
            <td>Purchased</td>
            <td class="vcr__calc"></td>
            <td class="vcr__n">{{ purchased }}</td>
          </tr>

          <!-- One "Used" line, never a per-action split. The refresh and track
               logbooks are a record of what was DRAWN, not an audited ledger of
               what was charged — the server debits the wallet and does not report
               the spend per action. Multiplying logbook lengths by catalogue
               prices only ever produced a plausible-looking figure that no system
               of record backs, so the dialog states the quantity it can stand
               behind: purchased, used, remaining. -->
          <tr class="vcr__row vcr__row--dim" *ngIf="remaining != null">
            <td><span class="vcr__dot is-used"></span>Used</td>
            <td class="vcr__calc"></td>
            <td class="vcr__n">{{ used }}</td>
          </tr>

          <!-- Approval zeroes the wallet, so an approved refund reports what was
               returned rather than the 0 that replaced it. -->
          <tr class="vcr__row vcr__row--dim" *ngIf="refundApproved">
            <td>Refunded</td>
            <td class="vcr__calc" *ngIf="showMoney">
              {{ refundedCredits }} &times; {{ unitCost | number: '1.2-2' }}
            </td>
            <td class="vcr__calc" *ngIf="!showMoney"></td>
            <td class="vcr__n">{{ refundedCredits }}</td>
          </tr>

          <tr class="vcr__row vcr__row--total">
            <td>Remaining</td>
            <td class="vcr__calc"></td>
            <td class="vcr__n">{{ remaining == null ? '—' : remaining }}</td>
          </tr>
          <tr class="vcr__row vcr__row--money" *ngIf="refundApproved && showMoney">
            <td>Refunded value</td>
            <td class="vcr__calc"></td>
            <td class="vcr__n">US$ {{ refundedValue | number: '1.2-2' }}</td>
          </tr>
        </table>
      </div>

      <!-- ══ section 2: refund ════════════════════════════════════════════ -->
      <!-- Nothing to request: the section still exists, so its absence is never
           mistaken for a missing feature — it states why instead. -->
      <div class="vcr__card vcr__card--state" *ngIf="!canRequestRefund">
        <!-- Both of these used to promise the credits were still spendable, which
             the TOI gate has made false in the case that actually occurs: a
             request can only be SUBMITTED once the TOI has finished, and past
             that point nothing on the task can be bought. The condition is kept
             rather than the sentence simply rewritten because an admin-set flag
             can still produce either state with the window open. -->
        <div class="vcr__banner is-wait" *ngIf="refundPending">
          <mat-icon class="material-icons-outlined">schedule</mat-icon>
          <span>Refund requested. {{ toiFinished
            ? 'The TOI has ended, so these credits cannot be spent while it is reviewed.'
            : 'Your credits stay spendable until it is approved.' }}</span>
        </div>
        <div class="vcr__banner is-good" *ngIf="refundApproved">
          <mat-icon class="material-icons-outlined">check_circle</mat-icon>
          <span>
            Refund approved. The refunded credits have been returned and can no longer be spent.
          </span>
        </div>
        <div class="vcr__banner is-bad" *ngIf="refundDeclined">
          <mat-icon class="material-icons-outlined">cancel</mat-icon>
          <span>Refund declined. {{ toiFinished
            ? 'The TOI has ended, so these credits can no longer be spent — please contact support if this is unexpected.'
            : 'Your credits remain available to spend.' }}</span>
        </div>
        <div class="vcr__banner is-info" *ngIf="allUsed && !refundPending && !refundApproved && !refundDeclined">
          <mat-icon class="material-icons-outlined">info</mat-icon>
          <span>All credits used. There is nothing left to refund — re-task to continue.</span>
        </div>
      </div>

      <form class="vcr__card" *ngIf="canRequestRefund" [formGroup]="formGroup" (ngSubmit)="onSubmit()">
        <!-- The amount leads the section: what is being requested, before the
             field asking to justify it. Money lives here, not in the usage
             ledger above — that one is denominated in credits. -->
        <div class="vcr__amount">
          <span class="vcr__amount-label">Refunding</span>
          <span class="vcr__amount-value">
            <b>{{ remaining }}</b> unused credits
            <span class="vcr__amount-money" *ngIf="showMoney">
              &middot; US$ {{ estimatedRefund | number: '1.2-2' }}
            </span>
          </span>
        </div>

        <p class="vcr__stranded" *ngIf="stranded">
          These credits are below the cost of any action, so they cannot be spent on this task.
        </p>

        <!-- Announced, because it is the reason the button below will not move. -->
        <p class="vcr__note is-block" *ngIf="!toiFinished" aria-live="polite">
          <mat-icon class="material-icons-outlined">schedule</mat-icon>
          <span>You can submit this once the time of interest (TOI) finishes.</span>
        </p>

        <label class="vcr__field-label" for="vcrReason">Reason for the request</label>
        <textarea
          id="vcrReason"
          class="vcr__reason"
          formControlName="reason"
          rows="4"
          [maxlength]="maxMsgLength"
          placeholder="Tell us why you are requesting this refund"
        ></textarea>

        <div class="vcr__counter">
          <span class="vcr__counter-err">{{ errorMessage }}</span>
          <span class="vcr__counter-n">{{ reasonLength }}/{{ maxMsgLength }}</span>
        </div>

        <p class="vcr__note">
          Final amount is confirmed on approval and reflects any credits spent in the meantime.
        </p>
      </form>
    </div>
  </div>

  <!-- Right-aligned above a rule: reads as the dialog's action bar rather than
       floating loose between sections. -->
  <div class="vcr__foot">
    <button class="custom-button" type="button" (click)="onCancel()">
      {{ canRequestRefund ? 'Cancel' : 'Close' }}
    </button>
    <button
      *ngIf="canRequestRefund"
      mat-stroked-button
      class="submit"
      type="button"
      [disabled]="!canSubmitRefund || formGroup.invalid || submitting"
      [matTooltip]="!toiFinished ? 'Available once the time of interest (TOI) finishes' : ''"
      (click)="onSubmit()"
    >
      {{ submitting ? 'Submitting…' : 'Request refund' }}
    </button>
  </div>
</div>
